import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '../../service/notification.service';
import { NotificationType } from '../../enum/notification-type.enum';
import { NgForm } from '@angular/forms';
import { TableColumn } from 'src/app/home/table/TableColumn';
import { GlobalService } from 'src/app/service/global.service';
import { UpdateCommissionsUiModel } from 'src/app/models/commissionsummary/update-commissions-ui.model';
import { UpdateCommissionsUiService } from 'src/app/service/commission-summary/update-commissions-ui.service';

interface HeaderSetTab {
  key: string;
  salesChannel: string;
  commPlanId: number;
  label: string;
}

@Component({
  selector: 'app-update-commissions-ui',
  templateUrl: './update-commissions-ui.component.html',
  styleUrls: ['./update-commissions-ui.component.css'],
})
export class UpdateCommissionsUiComponent implements OnInit, OnDestroy {

  @ViewChild('ngFormNav') ngFormNav: NgForm;
  @ViewChild('newForm') newForm: NgForm;

  constructor(
    private globalService: GlobalService,
    private httpService: HttpClient,
    private updateCommissionsUiService: UpdateCommissionsUiService,
    private notificationService: NotificationService
  ) { }

  public updateCommissionsUiModel = new UpdateCommissionsUiModel();
  public updateCommissionsUiEditModel = new UpdateCommissionsUiModel();

  public getConfigData: UpdateCommissionsUiModel[] = [];
  public reportTypesData: UpdateCommissionsUiModel[] = [];
  public dropDownsData: UpdateCommissionsUiModel[] = [];

  public filteredSalesChannelsData: UpdateCommissionsUiModel[] = [];

  public kpisData: UpdateCommissionsUiModel[] = [];
  public comPlanData: UpdateCommissionsUiModel[] = [];
  public filteredComPlanDataAdd: UpdateCommissionsUiModel[] = [];
  public filteredComPlanDataEdit: UpdateCommissionsUiModel[] = [];
  public filteredComPlanDataHeaders: UpdateCommissionsUiModel[] = [];
  public userRolesData: UpdateCommissionsUiModel[] = [];

  public headersEditModels: UpdateCommissionsUiModel[] = [];
  public allHeaderSetTabs: HeaderSetTab[] = [];
  public headerSetTabs: HeaderSetTab[] = [];
  public activeHeaderSetKey: string | null = null;
  private headersLoadSeq = 0;
  private headerSetLoadSeq = 0;
  private loadedHeaderSetKey: string | null = null;
  private headersTabLoaded = false;
  private buttonsTabLoaded = false;
  private buttonsLoadSeq = 0;
  private buttonsLoadedConfigId = 0;
  public buttonsEditModels: UpdateCommissionsUiModel[] = [];

  public adminConfigData: UpdateCommissionsUiModel[] = [];
  public feedTypesData: UpdateCommissionsUiModel[] = [];
  public kPITypeRevCommData: UpdateCommissionsUiModel[] = [];
  public kPITypeRevCommNavigationData: UpdateCommissionsUiModel[] = [];
  public navigationListData: UpdateCommissionsUiModel[] = [];
  public pendingDeleteNavigation: UpdateCommissionsUiModel | null = null;
  public navigationFormModel = new UpdateCommissionsUiModel();
  public filteredComPlanDataNav: UpdateCommissionsUiModel[] = [];


  message = '';
  subscriptions: Subscription[] = [];

  // GRID FRAMEWORK
  ordersTableColumns1: TableColumn[];
  columnsData: any;
  actions: any;
  actionButtons: any;
  isAccessDisabled: boolean = false;
  showLoading: boolean = false;
  jid: number = 0;

  private readonly permissionRoute = 'update-commissions-ui';

  ngOnInit(): void {
    this.isAccessDisabled = this.globalService._permissionCheck(this.permissionRoute);

    this.initializeColumns();
    this.refreshConfigGrid();
    this.getDropDownsData();
    this.getReportTypes();
    this.getKpis();
    this.getComPlans();
    this.getUserRoles();
    this.getAdminConfigData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private cloneComPlanList(): UpdateCommissionsUiModel[] {
    return JSON.parse(JSON.stringify(this.comPlanData));
  }

  private initFilteredComPlansForEdit(sc: string | null | undefined): void {
    if (!sc) {
      this.filteredComPlanDataEdit = this.cloneComPlanList();
      return;
    }
    this.filteredComPlanDataEdit = this.comPlanData.filter(item => item.saleschannel === sc);
  }

  private extractErrorMessage(errorResponse: HttpErrorResponse, fallback = 'Request failed'): string {
    if (typeof errorResponse?.error === 'string') {
      return errorResponse.error;
    }
    if (errorResponse?.error?.message) {
      return errorResponse.error.message;
    }
    return errorResponse?.message || fallback;
  }

  private notifyError(errorResponse: HttpErrorResponse, fallback = 'Request failed'): void {
    this.notificationService.sendNotification(
      NotificationType.ERROR,
      this.extractErrorMessage(errorResponse, fallback)
    );
  }

  private guardWriteAccess(): boolean {
    if (this.isAccessDisabled) {
      this.notificationService.sendNotification(NotificationType.WARNING, NotificationType.PERMISSIONS_MSG);
      return false;
    }
    return true;
  }

  /**
   * Normalizes scope before save:
   * 1) SC+CP+KPI → save all selected values
   * 2) SC+CP only → KPI=0
   * 3) SC only → CP=0, KPI=0
   * 4) none → SC=Global, CP=0, KPI=0
   */
  private normalizeScopeValues(
    salesChannelRaw: string | null | undefined,
    commPlanIdRaw: number | null | undefined,
    kpiIdRaw: number | null | undefined
  ): { saleschannel: string; commplanid: number; kpiId: number } {
    const salesChannel = (salesChannelRaw || '').trim();
    const commPlanId = Number(commPlanIdRaw) || 0;
    const kpiId = Number(kpiIdRaw) || 0;

    if (!salesChannel) {
      return { saleschannel: 'Global', commplanid: 0, kpiId: 0 };
    }
    if (commPlanId <= 0) {
      return { saleschannel: salesChannel, commplanid: 0, kpiId: 0 };
    }
    return {
      saleschannel: salesChannel,
      commplanid: commPlanId,
      kpiId: kpiId > 0 ? kpiId : 0
    };
  }

  private normalizeConfigScope(model: UpdateCommissionsUiModel): void {
    const normalized = this.normalizeScopeValues(model.saleschannel, model.commplanid, model.kpi_id);
    model.saleschannel = normalized.saleschannel;
    model.commplanid = normalized.commplanid;
    model.kpi_id = normalized.kpiId;
  }

  private normalizeNavigationScope(model: UpdateCommissionsUiModel): void {
    const normalized = this.normalizeScopeValues(model.saleschannel, model.commplanid, model.kpiid);
    model.saleschannel = normalized.saleschannel;
    model.commplanid = normalized.commplanid;
    model.kpiid = normalized.kpiId;
  }

  private getEmptyNavigationFormState(): UpdateCommissionsUiModel {
    const model = new UpdateCommissionsUiModel();
    model.commplanid = 0;
    model.kpiid = 0;
    return model;
  }

  private getNavigationFormResetValue(): Record<string, unknown> {
    return {
      nav_saleschannel: null,
      nav_commplanid: 0,
      nav_kpiid: 0,
      kpitype: null,
      nav_feedtype: null,
      nav_page_navigation: null,
    };
  }

  public resetNavigationForm(): void {
    Object.assign(this.navigationFormModel, this.getEmptyNavigationFormState());
    this.navigationFormModel.id = null;
    this.navigationFormModel.transid = null;
    this.filteredComPlanDataNav = this.cloneComPlanList();

    const formValue = this.getNavigationFormResetValue();
    queueMicrotask(() => {
      if (this.ngFormNav) {
        this.ngFormNav.resetForm(formValue);
      }
    });
  }

  private getEmptyAddConfigFormState(): UpdateCommissionsUiModel {
    const model = new UpdateCommissionsUiModel();
    model.commplanid = 0;
    model.kpi_id = 0;
    model.effective_date = null;
    return model;
  }

  private getAddConfigFormResetValue(): Record<string, unknown> {
    return {
      page_name: null,
      page_desc: null,
      grid_name: null,
      order_by: null,
      effective_date: '',
      report_type: null,
      user_type: null,
      saleschannel: null,
      commplanid: 0,
      kpi_id: 0,
      query: null,
    };
  }

  public resetConfigForm(): void {
    Object.assign(this.updateCommissionsUiModel, this.getEmptyAddConfigFormState());
    this.filteredComPlanDataAdd = this.cloneComPlanList();

    const formValue = this.getAddConfigFormResetValue();
    queueMicrotask(() => {
      if (this.newForm) {
        this.newForm.resetForm(formValue);
      }
    });
  }

  public getNavSalesChannelLabel(navRow: UpdateCommissionsUiModel): string {
    return navRow?.saleschannel?.trim() || 'Global';
  }

  public getNavCommissionPlanLabel(navRow: UpdateCommissionsUiModel): string {
    const commPlanId = Number(navRow?.commplanid) || 0;
    if (!commPlanId) {
      return 'Global';
    }
    const plan = this.comPlanData.find(item => Number(item.commplanid) === commPlanId);
    return plan?.commission_plan || `Plan ${commPlanId}`;
  }

  public getNavKpiLabel(navRow: UpdateCommissionsUiModel): string {
    const kpiId = Number(navRow?.kpiid) || 0;
    if (!kpiId) {
      return 'Global';
    }
    const kpi = this.kpisData.find(item => Number(item.kpi_id) === kpiId);
    return kpi?.kpi_name || `KPI ${kpiId}`;
  }

  private syncConfigPlanSelectionForSc(
    model: UpdateCommissionsUiModel,
    filteredPlans: UpdateCommissionsUiModel[]
  ): void {
    const commPlanId = Number(model.commplanid) || 0;
    if (!commPlanId) {
      model.kpi_id = 0;
      model.kpiid = 0;
      return;
    }
    const planValid = filteredPlans.some(item => Number(item.commplanid) === commPlanId);
    if (!planValid) {
      model.commplanid = 0;
      model.kpi_id = 0;
      model.kpiid = 0;
    }
  }

  public insertNavigation() {
    if (!this.guardWriteAccess()) {
      return;
    }

    this.normalizeNavigationScope(this.navigationFormModel);

    const payload: Pick<
      UpdateCommissionsUiModel,
      'saleschannel' | 'commplanid' | 'kpiid' | 'kpitype' | 'feedtype' | 'pagenavigation' | 'created_by' | 'created_dt'
    > = {
      saleschannel: this.navigationFormModel.saleschannel,
      commplanid: this.navigationFormModel.commplanid,
      kpiid: this.navigationFormModel.kpiid,
      kpitype: this.navigationFormModel.kpitype,
      feedtype: this.navigationFormModel.feedtype,
      pagenavigation: this.navigationFormModel.pagenavigation,
      created_by: localStorage.getItem('CURRENT_USER_NAME_SESSION'),
      created_dt: new Date(),
    };

    this.updateCommissionsUiService.insertNavigation(payload).subscribe(
      () => {
        this.getNavigationsList();
        queueMicrotask(() => this.resetNavigationForm());
        this.notificationService.sendNotification(NotificationType.SUCCESS, 'Inserted Successfully');
      },
      (errorResponse: HttpErrorResponse) => {
        this.notifyError(errorResponse, 'Something went wrong!');
        this.message = 'Something went wrong!';
      });
  }

  public getNavigationsList(): void {
    this.subscriptions.push(
      this.updateCommissionsUiService.getNavigationsList().subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          this.navigationListData = response;
        },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        }
      )
    );
  }

  public promptDeleteNavigation(navRow: UpdateCommissionsUiModel): void {
    if (!this.guardWriteAccess()) {
      return;
    }

    this.pendingDeleteNavigation = navRow;
    this.clickButton('nav-delete-confirm-open');
  }

  public cancelDeleteNavigation(): void {
    this.pendingDeleteNavigation = null;
  }

  public confirmDeleteNavigation(): void {
    if (!this.pendingDeleteNavigation?.transid) {
      return;
    }

    const id = this.pendingDeleteNavigation.transid;
    this.pendingDeleteNavigation = null;
    this.clickButton('nav-delete-confirm-close');
    this.deleteNavigationRow(id);
  }

  public deleteNavigationRow(id: number): void {
    if (!this.guardWriteAccess()) {
      return;
    }

    this.updateCommissionsUiService.deleteNavigation(
      id,
      localStorage.getItem('CURRENT_USER_NAME_SESSION')
    ).subscribe(
      () => {
        this.getNavigationsList();
        this.notificationService.sendNotification(NotificationType.SUCCESS, 'Deleted Successfully');
      },
      (errorResponse: HttpErrorResponse) => {
        this.notifyError(errorResponse, 'Something went wrong!');
      }
    );
  }

  private normalizeEffectiveDateForSave(model: UpdateCommissionsUiModel): void {
    const raw = model.effective_date as unknown;
    if (raw == null || raw === '') {
      model.effective_date = null;
      return;
    }
    if (typeof raw === 'string') {
      model.effective_date = raw.substring(0, 10) as any;
      return;
    }
    const parsed = new Date(raw as Date);
    if (Number.isNaN(parsed.getTime())) {
      model.effective_date = null;
      return;
    }
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    model.effective_date = `${year}-${month}-${day}` as any;
  }

  public insertConfig() {
    if (!this.guardWriteAccess()) {
      return;
    }

    this.updateCommissionsUiModel.created_by = localStorage.getItem('CURRENT_USER_NAME_SESSION');
    this.updateCommissionsUiModel.created_dt = new Date();
    this.updateCommissionsUiModel.status = 'Y';
    this.normalizeEffectiveDateForSave(this.updateCommissionsUiModel);
    this.normalizeConfigScope(this.updateCommissionsUiModel);

    this.updateCommissionsUiService.insertConfig(this.updateCommissionsUiModel).subscribe(
      event => {
        this.refreshConfigGrid();
        this.clickButton('new-trans-close');
        queueMicrotask(() => this.resetConfigForm());
        this.notificationService.sendNotification(NotificationType.SUCCESS, 'Inserted Successfully');
      },
      (errorResponse: HttpErrorResponse) => {
        this.notifyError(errorResponse, 'Something went wrong!');
        this.message = 'Something went wrong!';
      });
  }

  public updateConfig() {
    if (!this.guardWriteAccess()) {
      return;
    }

    this.updateCommissionsUiEditModel.updated_by = localStorage.getItem('CURRENT_USER_NAME_SESSION');
    this.normalizeEffectiveDateForSave(this.updateCommissionsUiEditModel);
    this.normalizeConfigScope(this.updateCommissionsUiEditModel);

    this.updateCommissionsUiService.updateConfig(this.updateCommissionsUiEditModel).subscribe(
      event => {
        this.refreshConfigGrid();
        this.notificationService.sendNotification(NotificationType.SUCCESS, 'Uploaded Successfully');
      },
      (errorResponse: HttpErrorResponse) => {
        this.notifyError(errorResponse, 'Failed To Update!');
        this.message = 'Failed To Update!';
      });

  }

  public updateHeaders() {
    if (!this.guardWriteAccess()) {
      return;
    }

    this.headersEditModels[0].updated_by = localStorage.getItem('CURRENT_USER_NAME_SESSION');
    this.headersEditModels[0].configid = this.jid;
    const savedSc = this.headersEditModels[0].header_saleschannel || 'Global';
    const savedCommPlanId = Number(this.headersEditModels[0].header_commplanid) || 0;
    this.updateCommissionsUiService.updateHeaders(this.headersEditModels)
      .subscribe((data: UpdateCommissionsUiModel[]) => {
        this.notificationService.sendNotification(NotificationType.SUCCESS, 'Updated Successfully');
        this.refreshHeaderSetsAfterSave(savedSc, savedCommPlanId);
      },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        });
  }

  public updateButtons() {
    if (!this.guardWriteAccess()) {
      return;
    }

    this.buttonsEditModels[0].updated_by = localStorage.getItem('CURRENT_USER_NAME_SESSION');
    this.buttonsEditModels[0].buttonconfigid = this.jid;
    this.updateCommissionsUiService.updateButtons(this.buttonsEditModels)
      .subscribe((data: UpdateCommissionsUiModel[]) => {
        this.notificationService.sendNotification(NotificationType.SUCCESS, 'Updated Successfully');
      },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        });
  }


  public getUserRoles(): void {
    this.subscriptions.push(
      this.updateCommissionsUiService.getUserRoles().subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          this.userRolesData = response;
        },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        }
      )
    );
  }

  public getAdminConfigData(): void {
    this.subscriptions.push(
      this.updateCommissionsUiService.getAdminConfigData().subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          this.adminConfigData = response;

          this.getKPITypeRevComm();
          this.getFeedTypes();
          this.getKPITypeRevCommNavigation();

        },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        }
      )
    );
  }

  public getKPITypeRevComm(): void {
    this.kPITypeRevCommData = this.adminConfigData.filter(comm => comm.field_name === 'KPI Type Simple/Complex');
    this.kPITypeRevCommData = this.kPITypeRevCommData.sort((a, b) => (a.field_value < b.field_value) ? -1 : 1);
  }

  public getFeedTypes(): void {
    this.feedTypesData = this.adminConfigData.filter(comm => comm.field_name === 'Feed Type');
    this.feedTypesData = this.feedTypesData.sort((a, b) => (a.field_value < b.field_value) ? -1 : 1);
  }

  public getKPITypeRevCommNavigation(): void {
    this.kPITypeRevCommNavigationData = this.adminConfigData.filter(comm => comm.field_name === 'Rev Comm Navigation');
    this.kPITypeRevCommNavigationData = this.kPITypeRevCommNavigationData.sort((a, b) => (a.field_value < b.field_value) ? -1 : 1);
  }

  public getKpis(): void {
    this.subscriptions.push(
      this.updateCommissionsUiService.getKpis().subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          this.kpisData = response;
        },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        }
      )
    );
  }



  public getDropDownsData(): void {
    this.subscriptions.push(
      this.updateCommissionsUiService.getDropDownsData().subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          this.dropDownsData = response;
          this.filteredSalesChannelsData = this.dropDownsData.filter((v, i) => this.dropDownsData.findIndex(item => item.saleschannel != "" && item.saleschannel == v.saleschannel) === i);
          this.filteredSalesChannelsData = this.filteredSalesChannelsData.sort((a, b) => (a.saleschannel < b.saleschannel) ? -1 : 1);
        },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        }
      )
    );
  }

  public getSelectedScNav(event: any): void {
    const sc = (event.target.value || '').trim();
    if (!sc) {
      this.filteredComPlanDataNav = this.cloneComPlanList();
      this.navigationFormModel.commplanid = 0;
      this.navigationFormModel.kpiid = 0;
      return;
    }
    this.filteredComPlanDataNav = this.comPlanData.filter(item => item.saleschannel === sc);
    this.syncConfigPlanSelectionForSc(this.navigationFormModel, this.filteredComPlanDataNav);
  }

  public getSelectedCommPlanNav(event: any): void {
    const commPlanId = Number(event.target.value) || 0;
    if (commPlanId <= 0) {
      this.navigationFormModel.kpiid = 0;
    }
  }

  public getSelectedScAdd(event: any) {
    const sc = (event.target.value || '').trim();
    if (!sc) {
      this.filteredComPlanDataAdd = this.cloneComPlanList();
      this.updateCommissionsUiModel.commplanid = 0;
      this.updateCommissionsUiModel.kpi_id = 0;
      return;
    }
    this.filteredComPlanDataAdd = this.comPlanData.filter(item => item.saleschannel === sc);
    this.syncConfigPlanSelectionForSc(this.updateCommissionsUiModel, this.filteredComPlanDataAdd);
  }

  public getSelectedScEdit(event: any) {
    const sc = (event.target.value || '').trim();
    if (!sc) {
      this.filteredComPlanDataEdit = this.cloneComPlanList();
      this.updateCommissionsUiEditModel.commplanid = 0;
      this.updateCommissionsUiEditModel.kpi_id = 0;
      return;
    }
    this.filteredComPlanDataEdit = this.comPlanData.filter(item => item.saleschannel === sc);
    this.syncConfigPlanSelectionForSc(this.updateCommissionsUiEditModel, this.filteredComPlanDataEdit);
  }

  public getSelectedCommPlanAdd(event: any): void {
    const commPlanId = Number(event.target.value) || 0;
    if (commPlanId <= 0) {
      this.updateCommissionsUiModel.kpi_id = 0;
    }
  }

  public getSelectedCommPlanEdit(event: any): void {
    const commPlanId = Number(event.target.value) || 0;
    if (commPlanId <= 0) {
      this.updateCommissionsUiEditModel.kpi_id = 0;
    }
  }

  public getSelectedHeaderSc(event: any) {
    const sc = event.target.value || 'Global';
    let commPlanId = Number(this.headersEditModels[0]?.header_commplanid) || 0;

    this.syncFilteredComPlansForHeaderSc(sc);
    if (sc !== 'Global' && commPlanId) {
      const planValid = this.filteredComPlanDataHeaders.some(
        item => Number(item.commplanid) === commPlanId
      );
      if (!planValid) {
        commPlanId = 0;
      }
    }

    if (this.headersEditModels[0]) {
      this.headersEditModels[0].header_saleschannel = sc;
      this.headersEditModels[0].header_commplanid = commPlanId;
    }

    this.updateVisibleHeaderSetTabs(sc, commPlanId);
    this.loadHeadersForSet(sc, commPlanId);
  }

  public getSelectedHeaderCommPlan(event: any) {
    const sc = this.headersEditModels[0]?.header_saleschannel || 'Global';
    const commPlanId = Number(event.target.value) || 0;

    if (this.headersEditModels[0]) {
      this.headersEditModels[0].header_commplanid = commPlanId;
    }

    this.updateVisibleHeaderSetTabs(sc, commPlanId);
    this.loadHeadersForSet(sc, commPlanId);
  }

  public loadHeaderSets(configId: number, selectKey?: string | null, forceHeaderReload = false): void {
    const loadSeq = ++this.headerSetLoadSeq;
    this.subscriptions.push(
      this.updateCommissionsUiService.fetchHeaderSets(configId).subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          if (loadSeq !== this.headerSetLoadSeq) {
            return;
          }
          this.applyHeaderSetsResponse(response, selectKey, forceHeaderReload);
        },
        (errorResponse: HttpErrorResponse) => {
          if (loadSeq !== this.headerSetLoadSeq) {
            return;
          }
          this.notifyError(errorResponse);
        }
      )
    );
  }

  private applyHeaderSetsResponse(
    response: UpdateCommissionsUiModel[],
    selectKey?: string | null,
    forceHeaderReload = false
  ): void {
    this.allHeaderSetTabs = this.sortHeaderSetTabs(response.map(set => {
      const salesChannel = (set.header_saleschannel || 'Global').trim();
      const commPlanId = Number(set.header_commplanid) || 0;
      return {
        key: this.getHeaderSetKey(salesChannel, commPlanId),
        salesChannel,
        commPlanId,
        label: this.buildHeaderSetLabel(salesChannel, commPlanId)
      };
    }));

    if (selectKey) {
      const [sc, commPlanIdStr] = selectKey.split('|');
      const salesChannel = sc || 'Global';
      const commPlanId = Number(commPlanIdStr) || 0;
      this.updateVisibleHeaderSetTabs(salesChannel, commPlanId);
      const selectedTab = this.headerSetTabs.find(tab => tab.key === selectKey);
      if (selectedTab) {
        this.selectHeaderSetTab(selectedTab, forceHeaderReload);
        return;
      }
      this.loadHeadersForSet(salesChannel, commPlanId, forceHeaderReload);
      return;
    }

    if (this.allHeaderSetTabs.length) {
      const firstTab = this.allHeaderSetTabs[0];
      this.updateVisibleHeaderSetTabs(firstTab.salesChannel, firstTab.commPlanId);
      if (this.headerSetTabs.length) {
        this.selectHeaderSetTab(this.headerSetTabs[0], forceHeaderReload);
      } else {
        this.loadHeadersForSet(firstTab.salesChannel, firstTab.commPlanId, forceHeaderReload);
      }
    } else {
      this.headerSetTabs = [];
      this.loadHeadersForSet('Global', 0, forceHeaderReload);
    }
  }

  public selectHeaderSetTab(tab: HeaderSetTab, forceReload = false): void {
    this.selectHeaderSetAfterLoad(tab, forceReload);
  }

  private selectHeaderSetAfterLoad(tab: HeaderSetTab, forceReload: boolean): void {
    this.activeHeaderSetKey = tab.key;
    this.syncFilteredComPlansForHeaderSc(tab.salesChannel);
    this.updateVisibleHeaderSetTabs(tab.salesChannel, tab.commPlanId);
    this.loadHeadersForSet(tab.salesChannel, tab.commPlanId, forceReload);
  }

  private updateVisibleHeaderSetTabs(sc?: string, commPlanId?: number): void {
    const salesChannel = sc || this.headersEditModels[0]?.header_saleschannel || 'Global';
    const planId = commPlanId !== undefined
      ? Number(commPlanId) || 0
      : Number(this.headersEditModels[0]?.header_commplanid) || 0;

    this.headerSetTabs = this.filterHeaderSetTabs(salesChannel, planId);
  }

  private filterHeaderSetTabs(sc: string, commPlanId: number): HeaderSetTab[] {
    const salesChannel = (sc || 'Global').trim();

    if (salesChannel === 'Global') {
      return this.allHeaderSetTabs.filter(tab => tab.salesChannel === 'Global');
    }

    return this.allHeaderSetTabs.filter(tab => {
      if (tab.salesChannel !== salesChannel) {
        return false;
      }
      if (!commPlanId) {
        return true;
      }
      return !tab.commPlanId || tab.commPlanId === commPlanId;
    });
  }

  private refreshHeaderSetsAfterSave(salesChannel: string, commPlanId: number): void {
    this.loadedHeaderSetKey = null;
    this.loadHeaderSets(this.jid, this.getHeaderSetKey(salesChannel, commPlanId), true);
  }

  private loadHeadersForSet(sc: string, commPlanId: number, forceReload = false): void {
    const salesChannel = (sc || 'Global').trim();
    const planId = Number(commPlanId) || 0;
    const configId = this.jid;

    if (!configId) {
      return;
    }

    const key = this.getHeaderSetKey(salesChannel, planId);
    if (!forceReload && key === this.loadedHeaderSetKey && this.hasPersistedHeaderRows()) {
      this.activeHeaderSetKey = key;
      return;
    }

    this.activeHeaderSetKey = key;
    const loadSeq = ++this.headersLoadSeq;
    const filterSnapshot = this.captureHeaderFilterFields();
    const placeholder = this.createEmptyHeaderRow(salesChannel, planId, configId);
    if (filterSnapshot) {
      placeholder.isdisputeview = filterSnapshot.isdisputeview ?? false;
      placeholder.colspancnt = filterSnapshot.colspancnt ?? 0;
    }
    this.headersEditModels = [placeholder];

    this.subscriptions.push(
      this.updateCommissionsUiService.fetchHeaders(configId, salesChannel, planId).subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          if (loadSeq !== this.headersLoadSeq) {
            return;
          }

          const rows = Array.isArray(response) ? response : [];
          if (rows.length === 0) {
            this.headersEditModels = [this.createEmptyHeaderRow(salesChannel, planId, configId)];
            this.applyHeaderFilterFields({
              ...(filterSnapshot || {}),
              header_saleschannel: salesChannel,
              header_commplanid: planId
            });
            this.loadedHeaderSetKey = key;
            return;
          }

          this.headersEditModels = rows;
          this.loadedHeaderSetKey = key;
        },
        (errorResponse: HttpErrorResponse) => {
          if (loadSeq !== this.headersLoadSeq) {
            return;
          }
          this.notifyError(errorResponse);
        }
      )
    );
  }

  private hasPersistedHeaderRows(): boolean {
    return this.headersEditModels.some(row => !!row?.pagename?.trim());
  }

  private createEmptyHeaderRow(sc: string, commPlanId: number, configId: number): UpdateCommissionsUiModel {
    const emptyHeaderRow = new UpdateCommissionsUiModel();
    emptyHeaderRow.header_saleschannel = sc;
    emptyHeaderRow.header_commplanid = commPlanId;
    emptyHeaderRow.margin = 'right';
    emptyHeaderRow.istotalview = false;
    emptyHeaderRow.ishyperlinkview = false;
    emptyHeaderRow.isdisputeview = false;
    emptyHeaderRow.colspancnt = 0;
    emptyHeaderRow.colorder = 1;
    emptyHeaderRow.configid = configId;
    return emptyHeaderRow;
  }

  private syncFilteredComPlansForHeaderSc(sc: string): void {
    if (sc === 'Global') {
      this.filteredComPlanDataHeaders = this.cloneComPlanList();
      return;
    }
    this.filteredComPlanDataHeaders = this.comPlanData.filter(item => item.saleschannel === sc);
  }

  private getHeaderSetKey(sc: string, commPlanId: number): string {
    return `${sc || 'Global'}|${Number(commPlanId) || 0}`;
  }

  private buildHeaderSetLabel(sc: string, commPlanId: number): string {
    const salesChannel = sc || 'Global';
    if (!commPlanId) {
      return salesChannel === 'Global' ? 'Global' : `${salesChannel} (all plans)`;
    }
    const plan = this.comPlanData.find(item => Number(item.commplanid) === Number(commPlanId));
    const planName = plan?.commission_plan || `Plan ${commPlanId}`;
    return `${salesChannel} / ${planName}`;
  }

  private sortHeaderSetTabs(tabs: HeaderSetTab[]): HeaderSetTab[] {
    return tabs.sort((a, b) => {
      if (a.salesChannel === 'Global' && !a.commPlanId) {
        return -1;
      }
      if (b.salesChannel === 'Global' && !b.commPlanId) {
        return 1;
      }
      const scCompare = a.salesChannel.localeCompare(b.salesChannel);
      if (scCompare !== 0) {
        return scCompare;
      }
      return a.commPlanId - b.commPlanId;
    });
  }

  public getComPlans(): void {
    this.subscriptions.push(
      this.updateCommissionsUiService.getComPlans().subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          this.comPlanData = response;
          const clonedPlans = JSON.parse(JSON.stringify(response));
          this.filteredComPlanDataAdd = clonedPlans;
          this.filteredComPlanDataEdit = clonedPlans;
          this.filteredComPlanDataHeaders = clonedPlans;
          this.filteredComPlanDataNav = clonedPlans;

        },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        }
      )
    );
  }

  public getReportTypes(): void {
    this.subscriptions.push(
      this.updateCommissionsUiService.getReportTypes().subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          this.reportTypesData = response;
        },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        }
      )
    );
  }

  public refreshConfigGrid(): void {
    this.subscriptions.push(
      this.updateCommissionsUiService.getConfigList().subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          this.getConfigData = response;
        },
        (errorResponse: HttpErrorResponse) => {
          this.notifyError(errorResponse);
        }
      )
    );
  }

  public getButtons(id: number) {
    const loadSeq = ++this.buttonsLoadSeq;
    this.subscriptions.push(
      this.updateCommissionsUiService.fetchButtons(id).subscribe(
        (response: UpdateCommissionsUiModel[]) => {
          if (loadSeq !== this.buttonsLoadSeq) {
            return;
          }
          this.buttonsEditModels = response;
          if (this.buttonsEditModels.length === 0) {
            const emptyButtonRow = new UpdateCommissionsUiModel();
            emptyButtonRow.buttonconfigid = id;
            emptyButtonRow.button_position = 'top';
            this.buttonsEditModels.push(emptyButtonRow);
          }
        },
        (errorResponse: HttpErrorResponse) => {
          if (loadSeq !== this.buttonsLoadSeq) {
            return;
          }
          this.notifyError(errorResponse);
        }
      )
    );
  }

  public onEditTabSelected(tab: 'general' | 'headers' | 'buttons'): void {
    if (tab === 'headers') {
      this.ensureHeadersTabLoaded();
    } else if (tab === 'buttons') {
      this.ensureButtonsTabLoaded();
    }
  }

  private ensureHeadersTabLoaded(): void {
    if (!this.jid) {
      return;
    }
    if (this.headersTabLoaded) {
      return;
    }
    this.headersTabLoaded = true;
    this.loadHeaderSets(this.jid);
  }

  private ensureButtonsTabLoaded(): void {
    if (!this.jid) {
      return;
    }
    if (this.buttonsTabLoaded && this.buttonsLoadedConfigId === this.jid && this.buttonsEditModels.length) {
      return;
    }
    this.buttonsTabLoaded = true;
    this.buttonsLoadedConfigId = this.jid;
    this.getButtons(this.jid);
  }

  addNewHeaderEditRow(form: NgForm) {
    const filterSnapshot = this.captureHeaderFilterFields();
    const headerObj = new UpdateCommissionsUiModel();
    headerObj.margin = 'right';
    headerObj.istotalview = false;
    headerObj.ishyperlinkview = false;
    headerObj.colorder = this.getNextHeaderColOrder();
    headerObj.configid = this.jid;

    if (filterSnapshot) {
      headerObj.header_saleschannel = filterSnapshot.header_saleschannel;
      headerObj.header_commplanid = filterSnapshot.header_commplanid;
      headerObj.header_kpiid = filterSnapshot.header_kpiid;
      headerObj.isdisputeview = filterSnapshot.isdisputeview;
      headerObj.colspancnt = filterSnapshot.colspancnt;
    }

    this.headersEditModels.push(headerObj);
  }

  private getNextHeaderColOrder(): number {
    if (!this.headersEditModels.length) {
      return 1;
    }
    const maxOrder = this.headersEditModels.reduce((max, row) => {
      const order = Number(row.colorder);
      return Number.isFinite(order) ? Math.max(max, order) : max;
    }, 0);
    return maxOrder + 1;
  }

  private captureHeaderFilterFields(): Partial<UpdateCommissionsUiModel> | null {
    if (!this.headersEditModels.length) {
      return null;
    }
    const first = this.headersEditModels[0];
    return {
      header_saleschannel: first.header_saleschannel,
      header_commplanid: first.header_commplanid,
      header_kpiid: first.header_kpiid,
      isdisputeview: first.isdisputeview,
      colspancnt: first.colspancnt,
      configid: first.configid
    };
  }

  private applyHeaderFilterFields(snapshot: Partial<UpdateCommissionsUiModel> | null): void {
    if (!snapshot || !this.headersEditModels.length) {
      return;
    }
    Object.assign(this.headersEditModels[0], snapshot);
  }

  public removeHeaderEditRow(index: number) {
    if (this.headersEditModels.length <= 1) {
      this.notificationService.sendNotification(NotificationType.WARNING, "There Is Only One Row Exist You Can't Delete.");
      return;
    }

    const filterSnapshot = this.captureHeaderFilterFields();
    this.headersEditModels.splice(index, 1);
    this.applyHeaderFilterFields(filterSnapshot);
  }

  addNewButtonsEditRow(kpiForm: NgForm) {
    const buttonObj = new UpdateCommissionsUiModel();
    buttonObj.button_position = 'top';
    buttonObj.buttonconfigid = this.jid;

    this.buttonsEditModels.push(buttonObj);
  }

  private captureButtonConfigFields(): Partial<UpdateCommissionsUiModel> | null {
    if (!this.buttonsEditModels.length) {
      return null;
    }
    const first = this.buttonsEditModels[0];
    return {
      buttonconfigid: first.buttonconfigid ?? this.jid
    };
  }

  private applyButtonConfigFields(snapshot: Partial<UpdateCommissionsUiModel> | null): void {
    if (!snapshot || !this.buttonsEditModels.length) {
      return;
    }
    Object.assign(this.buttonsEditModels[0], snapshot);
  }

  public removeButtonsEditRow(index: number) {
    if (this.buttonsEditModels.length <= 1) {
      this.notificationService.sendNotification(NotificationType.WARNING, "There Is Only One Row Exist You Can't Delete.");
      return;
    }

    const configSnapshot = this.captureButtonConfigFields();
    this.buttonsEditModels.splice(index, 1);
    this.applyButtonConfigFields(configSnapshot);
  }

  public onEditJson(updateEditModel: UpdateCommissionsUiModel): void {
    this.updateCommissionsUiEditModel = JSON.parse(JSON.stringify(updateEditModel));
    this.jid = updateEditModel.id;
    ++this.headerSetLoadSeq;
    ++this.headersLoadSeq;
    ++this.buttonsLoadSeq;
    this.headersEditModels = [];
    this.allHeaderSetTabs = [];
    this.headerSetTabs = [];
    this.activeHeaderSetKey = null;
    this.loadedHeaderSetKey = null;
    this.headersTabLoaded = false;
    this.buttonsTabLoaded = false;
    this.buttonsLoadedConfigId = 0;
    this.buttonsEditModels = [];
    this.initFilteredComPlansForEdit(this.updateCommissionsUiEditModel.saleschannel);
    this.filteredComPlanDataHeaders = this.cloneComPlanList();

    this.clickButton('Edit');
  }

  public onClone(id: number): void {
    if (!this.guardWriteAccess()) {
      return;
    }

    this.updateCommissionsUiService.onClone(id, localStorage.getItem('CURRENT_USER_NAME_SESSION')).subscribe(
      reponse => {
        this.refreshConfigGrid();
        this.notificationService.sendNotification(NotificationType.SUCCESS, 'Cloned Successfully');
      },
      (errorResponse: HttpErrorResponse) => {
        this.notifyError(errorResponse, 'Something went wrong!');
        this.message = 'Something went wrong!';
      });
  }

  private clickButton(buttonId: string): void {
    document.getElementById(buttonId).click();
  }

  innerRowAction1(row: any) {
    if (row.value === 'Update') {
      if (!this.guardWriteAccess()) {
        return;
      }
      this.onEditJson(row.row);
    } else if (row.value === 'Clone') {
      if (!this.guardWriteAccess()) {
        return;
      }
      this.onClone(row.row.id);
    }
  }

  actionOnButton(action: any) {
    if (action === "Add Query" || action === "Add Nav") {
      if (!this.guardWriteAccess()) {
        return;
      }
      if (action === 'Add Nav') {
        this.getNavigationsList();
        this.clickButton(action);
        queueMicrotask(() => this.resetNavigationForm());
        return;
      }
      if (action === 'Add Query') {
        this.clickButton(action);
        queueMicrotask(() => this.resetConfigForm());
        return;
      }
      this.clickButton(action);
    }
  }

  private buildRowActions(): any[] {
    if (this.isAccessDisabled) {
      return [];
    }
    return [
      {
        name: 'Clone',
        dataKey: 'fas fa-clone success f-25',
        position: 'left',
        isSortable: false,
        class: 'badge bg-danger'
      },
      {
        name: 'Update',
        dataKey: 'far fa-edit success f-25',
        position: 'left',
        isSortable: false,
        class: 'badge bg-success'
      }
    ];
  }

  private buildToolbarButtons(): any[] {
    if (this.isAccessDisabled) {
      return [];
    }
    return [
      {
        name: 'Add Query',
        class: 'btn btn-success btn-xs'
      },
      {
        name: 'Add Nav',
        class: 'btn btn-primary btn-xs'
      }
    ];
  }

  initializeColumns(): void {
    this.httpService.get('../../../assets/json/tableColumns.json').subscribe(
      data => {
        this.columnsData = data;
        this.ordersTableColumns1 = this.columnsData.reviewCommsConfig;
      }, (errorResponse: HttpErrorResponse) => {
        this.notifyError(errorResponse);
      }
    );

    this.actions = this.buildRowActions();
    this.actionButtons = this.buildToolbarButtons();
  }


}
